import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfirmActionModal } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface ResetConfirm {
  /** Pass to juneau as `confirmReset` — resolves once the user decides. */
  readonly confirmReset: () => Promise<boolean>;
  /** Render inside the chat tree so the dialog mounts. */
  readonly dialog: ReactNode;
}

/**
 * Asks the user before the conversation is wiped. The promise stays pending
 * while the dialog is open, so juneau only resets once the user confirms.
 */
export function useResetConfirm(): ResetConfirm {
  const t = useFeatureTranslation('aiChat');
  const [isOpen, setIsOpen] = useState(false);
  // Holds the pending decision so confirm/cancel can settle the same promise.
  const decideRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirmReset = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        decideRef.current = resolve;
        setIsOpen(true);
      }),
    []
  );

  const settle = useCallback((confirmed: boolean) => {
    setIsOpen(false);
    decideRef.current?.(confirmed);
    decideRef.current = null;
  }, []);

  // The dialog is mounted conditionally, so it can disappear mid-decision. Without
  // this, juneau's `await confirmReset()` would never settle and the reset would hang.
  useEffect(
    () => () => {
      decideRef.current?.(false);
      decideRef.current = null;
    },
    []
  );

  const dialog = isOpen ? (
    <ConfirmActionModal
      title={t('resetConfirm.title')}
      message={t('resetConfirm.message')}
      confirmLabel={t('resetConfirm.confirm')}
      variant='danger'
      testId='chat-reset-confirm'
      onConfirm={() => settle(true)}
      onClose={() => settle(false)}
    />
  ) : null;

  return { confirmReset, dialog };
}
